import { useSolidChip } from '@/hooks/use-theme-colors';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { PageLayout } from '@/components/ui/page-layout';
import {
  ACTIVATION_STATE_ACTION_REQUIRED,
  ACTIVATION_STATE_ACTIVE,
  ACTIVATION_STATE_ACTIVE_AUTOMATIC_ON,
  ACTIVATION_STATE_DISABLED,
  ACTIVATION_STATE_RESTRICTED,
  ACTIVATION_STATE_RESTRICTED_FIXABLE,
  ACTIVATION_STATE_VERIFYING,
  ACTIVATION_STEP_STRIPE,
  ACTIVATION_STEP_STRIPE_CHECKING,
  ACTIVATION_STEP_STRIPE_INCOMPLETE,
} from '@/content/payments';
import { useCanManagePaymentAccount } from '@/hooks/use-membership';
import {
  useOpenStripeOnboarding,
  useSyncPaymentAccount,
  useUpdatePaymentCollection,
} from '@/hooks/use-payment-account';
import {
  PAYMENT_ACCOUNT_STATUS,
  PAYMENT_COLLECTION_MODE,
  type PaymentAccount,
} from '@/types/payment-account';
import { getApiErrorMessage } from '@/utils/api-error';
import { showToast } from '@/store/toastStore';


function goBackToProfile() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/profile' as never);
}

function StateIcon({
  name,
  color = '#FD5001',
  bg = 'bg-primary/10',
}: {
  name: React.ComponentProps<typeof Ionicons>['name'];
  color?: string;
  bg?: string;
}) {
  const solid = useSolidChip();
  return (
    <View
      className={`h-14 w-14 items-center justify-center rounded-full ${solid.cls(bg)}`}
      style={solid.style(color)}
    >
      <Ionicons name={name} size={26} color={solid.icon(color)} />
    </View>
  );
}

function CenteredState({
  icon,
  iconColor,
  iconBg,
  title,
  body,
  children,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  iconColor?: string;
  iconBg?: string;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <View className="items-center gap-4 py-10">
      <StateIcon name={icon} color={iconColor} bg={iconBg} />
      <View className="gap-2">
        <ThemedText className="text-center font-bold text-lg text-neutral-900">{title}</ThemedText>
        <ThemedText className="text-center text-sm leading-5 text-neutral-500">{body}</ThemedText>
      </View>
      {children}
    </View>
  );
}

/**
 * Cadastro no Stripe (conta `DRAFT`, ou link vencido em qualquer status).
 * No caminho B o Stripe já obriga a sair para
 * `connect.stripe.com` confirmar o login, então o cadastro inteiro (contrato
 * e documento) acontece lá, numa ida só (`useOpenStripeOnboarding`).
 * `?stripe=refresh` chega quando o link venceu: pede outro sozinho.
 */
function StripeOnboarding({ checking }: { checking: boolean }) {
  const { stripe } = useLocalSearchParams<{ stripe?: string }>();
  const openOnboarding = useOpenStripeOnboarding();
  const cameBack = stripe === 'done';

  const open = () =>
    openOnboarding.mutate(undefined, {
      onError: (err) =>
        showToast(getApiErrorMessage(err, 'Não foi possível abrir o cadastro.'), 'error'),
    });

  useEffect(() => {
    if (stripe === 'refresh') open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  if (cameBack && checking) {
    return (
      <PageLayout title={ACTIVATION_STEP_STRIPE.title} onBack={goBackToProfile}>
        <CenteredState
          icon="time-outline"
          title={ACTIVATION_STEP_STRIPE_CHECKING.title}
          body={ACTIVATION_STEP_STRIPE_CHECKING.body}
        />
      </PageLayout>
    );
  }

  // Voltou mas a conta segue sem cadastro concluído: faltou alguma etapa lá.
  const copy = cameBack ? ACTIVATION_STEP_STRIPE_INCOMPLETE : ACTIVATION_STEP_STRIPE;

  return (
    <PageLayout title={ACTIVATION_STEP_STRIPE.title} onBack={goBackToProfile}>
      <CenteredState
        icon={cameBack ? 'alert-circle-outline' : 'shield-checkmark-outline'}
        title={copy.intro}
        body={copy.helper}
      >
        <LoadingButton
          title={copy.cta}
          size="lg"
          loading={openOnboarding.isPending}
          onPress={open}
        />
      </CenteredState>
    </PageLayout>
  );
}

function VerifyingScreen() {
  return (
    <PageLayout title={ACTIVATION_STATE_VERIFYING.title} onBack={goBackToProfile}>
      <CenteredState
        icon="time-outline"
        title={ACTIVATION_STATE_VERIFYING.title}
        body={ACTIVATION_STATE_VERIFYING.body}
      >
        <Button
          title="Voltar para Como funciona"
          variant="outline"
          size="lg"
          className="w-full"
          onPress={() => router.replace('/(tabs)/(provider)/payments/about' as never)}
        />
      </CenteredState>
    </PageLayout>
  );
}

/**
 * "Pronto!" (T4.5): a conta já está `ACTIVE`. `GET /payment-account` agora
 * devolve `paymentCollection` (`0380a3a`) — com `AUTOMATIC`, o recebimento já
 * está ligado e esta tela só confirma o estado, sem repetir a oferta de
 * ligar (`ActiveAlreadyOnScreen` abaixo). Com `MANUAL`, oferece ligar:
 * `PATCH /payment-account/collection` (T2.4) muda `Provider.paymentCollection`.
 * Os dois botões desse fluxo ligam sempre — a diferença entre eles é só
 * `convertOpen` (passar ou não as cobranças em aberto para automático),
 * nunca risco de "religar" algo à revelia: é sempre um toque explícito do
 * prestador. `canManage` (dono/self, `useMembership().can('ownPayment')`,
 * T7.1) esconde os botões de quem só pode olhar (equipe) — só o dono liga o
 * automático, mesma regra do `PATCH` no servidor (`assertCanActivate`).
 */
function ActiveAlreadyOnScreen() {
  return (
    <PageLayout title={ACTIVATION_STATE_ACTIVE.title} onBack={goBackToProfile}>
      <CenteredState
        icon="checkmark-circle-outline"
        iconColor="#137A63"
        iconBg="bg-success-50"
        title={ACTIVATION_STATE_ACTIVE.title}
        body={ACTIVATION_STATE_ACTIVE_AUTOMATIC_ON.body}
      >
        <Button
          title={ACTIVATION_STATE_ACTIVE_AUTOMATIC_ON.receivablesButton}
          size="lg"
          className="w-full"
          onPress={() => router.push('/(tabs)/(provider)/payments' as never)}
        />
      </CenteredState>
    </PageLayout>
  );
}

function ActiveScreen({ account, canManage }: { account: PaymentAccount; canManage: boolean }) {
  const updateCollection = useUpdatePaymentCollection();
  const [resolved, setResolved] = useState(false);

  if (canManage && account.paymentCollection === PAYMENT_COLLECTION_MODE.AUTOMATIC) {
    return <ActiveAlreadyOnScreen />;
  }

  const activate = async (convertOpen: boolean) => {
    try {
      await updateCollection.mutateAsync({ mode: PAYMENT_COLLECTION_MODE.AUTOMATIC, convertOpen });
      setResolved(true);
      showToast(
        convertOpen
          ? 'Pronto! Cobranças em aberto passando para automático.'
          : 'Pronto! As próximas cobranças já são automáticas.',
        'success',
      );
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível ligar agora. Tente de novo.'), 'error');
    }
  };

  return (
    <PageLayout title={ACTIVATION_STATE_ACTIVE.title} onBack={goBackToProfile}>
      <CenteredState
        icon="checkmark-circle-outline"
        iconColor="#137A63"
        iconBg="bg-success-50"
        title={ACTIVATION_STATE_ACTIVE.title}
        body={ACTIVATION_STATE_ACTIVE.body}
      >
        {!canManage || resolved || !account.chargesEnabled ? (
          <Button
            title="Ver Como funciona"
            variant="outline"
            size="lg"
            className="w-full"
            onPress={() => router.replace('/(tabs)/(provider)/payments/about' as never)}
          />
        ) : (
          <View className="w-full gap-3">
            <ThemedText className="text-center text-sm text-neutral-600">
              {ACTIVATION_STATE_ACTIVE.convertOpenPrompt}
            </ThemedText>
            <LoadingButton
              title={ACTIVATION_STATE_ACTIVE.convertOpenButton}
              size="lg"
              className="w-full"
              loading={updateCollection.isPending}
              disabled={updateCollection.isPending}
              onPress={() => void activate(true)}
              testID="payment-active-convert-open"
            />
            <Button
              title={ACTIVATION_STATE_ACTIVE.skipButton}
              variant="outline"
              size="lg"
              className="w-full"
              disabled={updateCollection.isPending}
              onPress={() => void activate(false)}
              testID="payment-active-skip-convert"
            />
          </View>
        )}
      </CenteredState>
    </PageLayout>
  );
}

function ActionRequiredScreen({
  account,
  canManage,
  onResume,
  resuming,
}: {
  account: PaymentAccount;
  canManage: boolean;
  onResume: () => void;
  resuming: boolean;
}) {
  const items = [
    ...(account.requirementsDue?.pastDue ?? []),
    ...(account.requirementsDue?.currentlyDue ?? []),
  ].filter((item, index, all) => all.indexOf(item) === index);
  const deadline = account.requirementsDue?.currentDeadline;

  return (
    <PageLayout title={ACTIVATION_STATE_ACTION_REQUIRED.title} onBack={goBackToProfile}>
      <CenteredState
        icon="alert-circle-outline"
        iconColor="#D97706"
        iconBg="bg-warning-50"
        title={ACTIVATION_STATE_ACTION_REQUIRED.title}
        body={ACTIVATION_STATE_ACTION_REQUIRED.body}
      >
        {items.length > 0 ? (
          <View
            className="w-full gap-2 rounded-2xl border border-neutral-900/5 bg-card p-4"
            style={{ borderCurve: 'continuous' }}
          >
            {items.map((item) => (
              <View key={item} className="flex-row items-start gap-2">
                <Ionicons className="text-warning-600" name="ellipse" size={6} style={{ marginTop: 7 }} />
                <ThemedText className="flex-1 text-sm leading-5 text-neutral-700">
                  {item}
                </ThemedText>
              </View>
            ))}
            {deadline ? (
              <ThemedText className="pt-1 text-xs text-neutral-500">
                {ACTIVATION_STATE_ACTION_REQUIRED.deadlinePrefix}{' '}
                {new Date(deadline).toLocaleDateString('pt-BR')}
              </ThemedText>
            ) : null}
          </View>
        ) : null}
        {canManage ? (
          <LoadingButton
            title={ACTIVATION_STATE_ACTION_REQUIRED.resumeButton}
            size="lg"
            className="w-full"
            loading={resuming}
            onPress={onResume}
            testID="payment-action-required-resume"
          />
        ) : null}
      </CenteredState>
    </PageLayout>
  );
}

/**
 * `RESTRICTED` tem dois casos: o Stripe pausou por risco (nada a fazer
 * aqui) ou reprovou/venceu algum dado (`pastDue`/`currentlyDue`, ex.:
 * endereço não verificado). No segundo, quem gerencia corrige no cadastro
 * do Stripe, pelo mesmo caminho de "Resolver pendência".
 */
function RestrictedScreen({
  account,
  canManage,
  onResume,
  resuming,
}: {
  account: PaymentAccount;
  canManage: boolean;
  onResume: () => void;
  resuming: boolean;
}) {
  const due = account.requirementsDue;
  const fixable = canManage && !!(due?.pastDue?.length || due?.currentlyDue?.length);
  const copy = fixable ? ACTIVATION_STATE_RESTRICTED_FIXABLE : ACTIVATION_STATE_RESTRICTED;

  return (
    <PageLayout title={ACTIVATION_STATE_RESTRICTED.title} onBack={goBackToProfile}>
      <CenteredState
        icon="warning-outline"
        iconColor="#DC2626"
        iconBg="bg-error-50"
        title={copy.title}
        body={copy.body}
      >
        {fixable ? (
          <LoadingButton
            title={ACTIVATION_STATE_RESTRICTED_FIXABLE.cta}
            size="lg"
            loading={resuming}
            onPress={onResume}
          />
        ) : null}
      </CenteredState>
    </PageLayout>
  );
}

function DisabledScreen() {
  return (
    <PageLayout title={ACTIVATION_STATE_DISABLED.title} onBack={goBackToProfile}>
      <CenteredState
        icon="pause-circle-outline"
        iconColor="#737373"
        iconBg="bg-neutral-100"
        title={ACTIVATION_STATE_DISABLED.title}
        body={ACTIVATION_STATE_DISABLED.body}
      >
        <Button
          title="Ver Como funciona"
          variant="outline"
          size="lg"
          className="w-full"
          onPress={() => router.replace('/(tabs)/(provider)/payments/about' as never)}
        />
      </CenteredState>
    </PageLayout>
  );
}

/**
 * Estados pós-ativação (T4.5, plano seção 6.3): a conta já existe
 * (`POST /payment-account` já rodou, em qualquer status). `DRAFT` mostra
 * direto o componente do Stripe (falta ele confirmar); os outros mostram o
 * estado e, em `ACTION_REQUIRED`/`RESTRICTED` com dados pendentes, abrem o
 * cadastro do Stripe direto do botão — as duas ações só para quem pode gerenciar
 * (`canManage`, dono/self); quem só acompanha (equipe) nunca abre o
 * componente do Stripe, que é sempre do dono. `canManage` vem de
 * `useCanManagePaymentAccount` (T7.2), não de `can('ownPayment')`: essa
 * capability não considera "agindo como" outro profissional.
 */
export function PaymentAccountStatusScreen({ account }: { account: PaymentAccount }) {
  const canManage = useCanManagePaymentAccount();
  const { stripe } = useLocalSearchParams<{ stripe?: string }>();
  const sync = useSyncPaymentAccount();
  const openOnboarding = useOpenStripeOnboarding();

  // "Resolver pendência"/"Corrigir dados" vão direto pro Stripe: a tela já
  // explicou o que falta, a de "Confirmação" do cadastro seria um passo a mais.
  const resume = () =>
    openOnboarding.mutate(undefined, {
      onError: (err) =>
        showToast(getApiErrorMessage(err, 'Não foi possível abrir o cadastro.'), 'error'),
    });

  // Toda abertura confere a conta direto no Stripe, sem depender do webhook
  // `account.updated` ter chegado (pendência nova, reprovação, aprovação).
  // A resposta substitui o cache, e `account` chega atualizado pelo pai.
  useEffect(() => {
    if (canManage) sync.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  // `?stripe=refresh`: o link venceu no meio do cadastro — a tela do cadastro
  // pede outro sozinha, qualquer que seja o status.
  if (canManage && (stripe === 'refresh' || account.status === PAYMENT_ACCOUNT_STATUS.DRAFT)) {
    return <StripeOnboarding checking={sync.isIdle || sync.isPending} />;
  }

  switch (account.status) {
    case PAYMENT_ACCOUNT_STATUS.DRAFT:
    case PAYMENT_ACCOUNT_STATUS.VERIFYING:
      // DRAFT aqui só acontece pra quem não gerencia (nunca abre o
      // componente sozinho) ou bem depois de sair dele, antes do webhook
      // atualizar o status (corrida) — mesma tela de espera de VERIFYING é
      // o fallback mais seguro.
      return <VerifyingScreen />;
    case PAYMENT_ACCOUNT_STATUS.ACTIVE:
      return <ActiveScreen account={account} canManage={canManage} />;
    case PAYMENT_ACCOUNT_STATUS.ACTION_REQUIRED:
      return (
        <ActionRequiredScreen
          account={account}
          canManage={canManage}
          onResume={resume}
          resuming={openOnboarding.isPending}
        />
      );
    case PAYMENT_ACCOUNT_STATUS.RESTRICTED:
      return (
        <RestrictedScreen
          account={account}
          canManage={canManage}
          onResume={resume}
          resuming={openOnboarding.isPending}
        />
      );
    case PAYMENT_ACCOUNT_STATUS.DISABLED:
      return <DisabledScreen />;
    default:
      return <VerifyingScreen />;
  }
}
